/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { DownloadCardFragment } from "@cosmos/hygraph-schema";
import { cx, cva } from "@panda/css";
import { VStack } from "@panda/jsx";
import { grid } from "@panda/patterns";
import { RecipeVariantProps } from "@panda/types";
import { DownloadFooterCard } from "./DownloadFooterCard.molecule";
import { DownloadStep, DownloadStepProps } from "./DownloadStep.molecule";

const downloadStepsStyle = cva({
	base: {
		w: "full",
		h: "full",
		gridAutoRows: "1fr",
	},

	variants: {
		steps: {
			1: {
				gridTemplateColumns: {
					base: "1fr",
					lgDown: "1fr",
				},
			},
			2: {
				gridTemplateColumns: {
					base: "1fr 1fr",
					smDown: "1fr",
				},
			},
			3: {
				gridTemplateColumns: {
					base: "1fr 1fr 1fr",
					lgDown: "1fr 1fr",
					smDown: "1fr",
				},
			},
		},
		hasWideSteps: {
			true: {},
		},
	},
	compoundVariants: [
		{
			steps: 3,
			hasWideSteps: true,
			css: {
				gridTemplateColumns: "1fr 1fr 1fr 1fr",
				"& .download-step.wide": {
					gridColumn: 2,
				},
				xxlDown: {
					gridTemplateColumns: "1fr 1fr",
					gridTemplateRows: "1fr 1fr",
				},
				smDown: {
					gridTemplateColumns: "1fr",
					gridTemplateRows: "1fr 1fr 1fr 1fr",
					"& .download-step.wide": {
						gridColumn: 1,
						gridRow: 2,
					},
				},
			},
		},
	],
	defaultVariants: {
		steps: 3,
	},
});

export type DownloadStepsProps = {
	steps?: DownloadStepProps[] | null;
	footer?: DownloadCardFragment | null;
};

export function DownloadSteps({ steps = [], footer }: DownloadStepsProps): React.JSX.Element | null {
	if (!steps?.length) return null;

	// Max 3 steps per row; extra steps wrap onto new rows.
	const stepCount = Math.min(steps.length, 3);
	const hasWideSteps = steps?.some((step) => step.content.length > 1);
	return (
		<VStack alignItems="stretch" gap="eds.dimension.16">
			<div
				className={cx(
					"download-steps",
					hasWideSteps ? "has-wide-steps" : "",
					grid({
						gap: "eds.dimension.16",
					}),
					downloadStepsStyle({ steps: stepCount, hasWideSteps } as RecipeVariantProps<typeof downloadStepsStyle>),
				)}
			>
				{steps
					? steps.map((step, i) => (
							<DownloadStep key={`step-${i}`} className={step?.content?.length > 1 ? "wide" : undefined} {...step} />
						))
					: undefined}
			</div>
			{!footer ? undefined : <DownloadFooterCard card={footer} />}
		</VStack>
	);
}
